import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ShowConditionsButton from '@Components/Results/reactions/reaction-details/ShowConditionsButton';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ShowMore from '@Components/Results/utils/ShowMore';
import ReactReactionAdditionalInfoHelper from '@Components/Results/services/react-reaction-additional-info-helper.service';
import ReactionDetailRow from '@Components/Results/reactions/reaction-details/ReactionDetailRow';

import './ReactionDetailsTable.scss';

function ReactionDetailsTable({
  metadata,
  reactionId,
  details,
  detailsCount,
  initialDetailsNumber,
  isHitDetails,
  hasHitDetails,
  spinnerId,
  index,
  showMoreHandler,
  showLessHandler,
  showNextConditions,
  toggleAllDetails,
  databaseId,
  displayFooter,
  largestProduct,
}) {
  const tableRef = useRef();
  const isCIDatabase = ReactReactionAdditionalInfoHelper.isCIDatabase(databaseId);
  const containerClass = isHitDetails ? 'reaction-hit-details' : 'reaction-details';
  const { fromRT } = metadata || {};
  const showReferenceColumn = !metadata?.fromDocuments;

  useEffect(() => {
    if (!isHitDetails && hasHitDetails && tableRef.current) {
      tableRef.current.scrollTo({ top: tableRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [isHitDetails, hasHitDetails]);

  const allowShowMore = !metadata.isViewPreview || isHitDetails;
  const rows = details.map((detail, idx) => (
    <ReactionDetailRow
      metadata={metadata}
      key={detail.id || detail.conditionId || `${reactionId}-${idx}`}
      detail={detail}
      index={index}
      reactionId={reactionId}
      isHit={isHitDetails || detail.isHit}
      spinnerId={spinnerId}
      databaseId={databaseId}
      largestProduct={largestProduct}
      showReferenceColumn={showReferenceColumn}
      summaryConditionId={!fromRT && (isHitDetails || !hasHitDetails) ? `${reactionId}-${idx + 1}` : undefined}
    />
  ));

  return (
    <>
      <div className="rx-reflow-scrollable reaction-details-table-scroll">
        <table
          ref={tableRef}
          id={`${containerClass}-${index}`}
          className={cn(
            'rx-reactions-table rx-table--full-width rx-table--full-height c-els-table c-els-table--vertical-dividers c-els-table--subtle-dividers',
            { 'c-els-table--highlight-header': !fromRT }
          )}>
          <thead className="c-els-table__head">
            <tr className={cn('c-els-table__row c-els-table__row--head', { 'c-els-table__row--sticky': fromRT })}>
              <th className="rx-reactions-table__conditions c-els-table__cell c-els-table__basic-cell c-els-table__cell--header">
                Conditions
              </th>
              <th className="rx-reactions-table__yield c-els-table__cell c-els-table__basic-cell c-els-table__cell--header">Yield</th>
              {showReferenceColumn && (
                <th className="rx-reactions-table__references u-els-display-none@tablet c-els-table__cell c-els-table__basic-cell c-els-table__cell--header">
                  {isCIDatabase ? 'Reference/Experiment' : 'Reference'}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="c-els-table__body">{rows}</tbody>
        </table>
      </div>
      {displayFooter && initialDetailsNumber < detailsCount && (
        <div className="rx-btn-group--center u-els-margin-top-1o2">
          {allowShowMore && (
            <ShowMore
              hideIcon
              labelAll={`Show all ${isHitDetails ? 'hits' : 'conditions'}`}
              initial={initialDetailsNumber}
              current={details.length}
              total={detailsCount}
              container={`#${containerClass}-${index}`}
              scrollToLess={`#${containerClass}-${index}`}
              moreHandler={(args) => showMoreHandler(args)}
              lessHandler={() => showLessHandler()}
            />
          )}
          {allowShowMore && showNextConditions && (
            <ShowConditionsButton toggleAllDetails={toggleAllDetails} showNextConditions={showNextConditions} index={index} />
          )}
        </div>
      )}
    </>
  );
}

ReactionDetailsTable.defaultProps = {
  displayFooter: true,
};

ReactionDetailsTable.propTypes = {
  metadata: MetadataPropTypesShape,
  reactionId: PropTypes.string.isRequired,
  details: PropTypes.array.isRequired,
  detailsCount: PropTypes.number.isRequired,
  initialDetailsNumber: PropTypes.number,
  isHitDetails: PropTypes.bool,
  hasHitDetails: PropTypes.bool,
  spinnerId: PropTypes.string.isRequired,
  index: PropTypes.string.isRequired,
  showMoreHandler: PropTypes.func.isRequired,
  showLessHandler: PropTypes.func.isRequired,
  showNextConditions: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  toggleAllDetails: PropTypes.func,
  databaseId: PropTypes.string.isRequired,
  displayFooter: PropTypes.bool,
  largestProduct: PropTypes.string,
};

export default ReactionDetailsTable;
